import {
  AspectRatio,
  Box,
  Button,
  Center,
  Group,
  Image,
  SegmentedControl,
  Text,
  rem
} from '@mantine/core'
import { IconDownload, IconPlayerPlay } from '@tabler/icons-react'
import { SyntheticEvent, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ImagePlaceholder from '@/assets/image-placeholder.png'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { showToast } from '@/theme/notifications'
import { EventRecordingMode, EventVideoCodec } from '@/types/event'
import {
  canPlayHevc,
  formatEventOffset,
  isHevcRecording,
  isRecordingUnavailable
} from './recordingHelpers'

type MediaType = 'video' | 'image'

type MediaItemProps = {
  eventId: string
  video_path: string
  video_bbox_path: string
  image_path: string
  type: MediaType
  recording_mode?: EventRecordingMode
  video_codec?: EventVideoCodec | null
  trigger_offset_ms?: number | null
}

export const MediaItem = ({
  eventId,
  video_path,
  video_bbox_path,
  image_path,
  type,
  recording_mode,
  video_codec,
  trigger_offset_ms
}: MediaItemProps) => {
  const intl = useIntl()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [videoType, setVideoType] = useState<'original' | 'labeled'>('original')
  const [hasPlaybackError, setHasPlaybackError] = useState(false)

  const handleVideoDownload = async (name: string, path: string) => {
    setIsDownloading(true)

    try {
      const response = await fetch(path)
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)

      const anchor = document.createElement('a')
      anchor.href = url
      anchor.setAttribute('download', `${name}.mp4`)
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
    } catch {
      const message = intl.formatMessage({ id: 'events.download.video.error' })
      showToast(message, 'error')
    }

    setIsDownloading(false)
  }

  const handleLoadedMetadata = (event: SyntheticEvent<HTMLVideoElement>) => {
    if (event.currentTarget.videoWidth === 0) {
      setHasPlaybackError(true)
    }
  }

  const seekToEvent = () => {
    if (videoRef.current && typeof trigger_offset_ms === 'number') {
      videoRef.current.currentTime = trigger_offset_ms / 1000
    }
  }

  const activeVideoPath =
    videoType === 'original' ? video_path : video_bbox_path

  if (isRecordingUnavailable({ recording_mode, video_path })) {
    return (
      <AspectRatio ratio={16 / 9}>
        <Center bg="gray.1" style={{ borderRadius: 8 }}>
          <Text size="sm" c="dimmed">
            <FormattedMessage id="events.video.recordingUnavailable" />
          </Text>
        </Center>
      </AspectRatio>
    )
  }

  if (!image_path && !video_path && !video_bbox_path) {
    return null
  }

  const cannotPlay =
    hasPlaybackError || (isHevcRecording({ video_codec }) && !canPlayHevc())

  return (
    <Box pos="relative">
      <AspectRatio ratio={16 / 9}>
        {type === 'video' ? (
          <Box>
            <video
              key={activeVideoPath}
              ref={videoRef}
              width="100%"
              style={{ borderRadius: 8 }}
              aria-label={intl.formatMessage({ id: 'events.recording' })}
              controls
              onError={() => setHasPlaybackError(true)}
              onLoadedMetadata={handleLoadedMetadata}
            >
              <source
                src={activeVideoPath}
                type="video/mp4"
                onError={() => setHasPlaybackError(true)}
              />
            </video>
          </Box>
        ) : (
          <Box>
            <Image
              src={image_path}
              fallbackSrc={ImagePlaceholder}
              radius={8}
              h="100%"
              fit="contain"
            />
          </Box>
        )}
      </AspectRatio>

      {type === 'video' && (
        <Box pos="absolute" top={8} right={8}>
          <Group>
            {cannotPlay && (
              <PlatformAlert variant="warning" maw={360} py={4} compact>
                <FormattedMessage id="events.video.hevcUnsupported" />
              </PlatformAlert>
            )}

            {typeof trigger_offset_ms === 'number' && (
              <Button
                size="compact-xs"
                variant="white"
                color="brand-secondary"
                leftSection={<IconPlayerPlay size={12} />}
                onClick={seekToEvent}
              >
                <FormattedMessage
                  id="events.video.eventAt"
                  values={{ time: formatEventOffset(trigger_offset_ms) }}
                />
              </Button>
            )}

            {video_path && video_bbox_path && (
              <SegmentedControl
                color="brand-secondary"
                size="xs"
                withItemsBorders={false}
                value={videoType}
                styles={{
                  root: {
                    padding: '2px'
                  },
                  innerLabel: {
                    fontSize: rem(11),
                    lineHeight: 'normal' // gets overriden by image gallery styles, so we need to reset it
                  }
                }}
                data={[
                  {
                    value: 'original',
                    label: intl.formatMessage({ id: 'events.video.original' })
                  },
                  {
                    value: 'labeled',
                    label: intl.formatMessage({ id: 'events.video.labeled' })
                  }
                ]}
                onChange={(value) => {
                  setHasPlaybackError(false)
                  setVideoType(value as 'original' | 'labeled')
                }}
              />
            )}

            <Button
              size="compact-xs"
              color="brand-secondary"
              leftSection={<IconDownload size={12} />}
              loading={isDownloading}
              onClick={() => void handleVideoDownload(eventId, activeVideoPath)}
            >
              <FormattedMessage id="download" />
            </Button>
          </Group>
        </Box>
      )}
    </Box>
  )
}
